/**
 * Query keys for the sessions section.
 *
 * Every key starts with the active clinic id, for the reason spelled out in
 * `src/api/query-keys.ts`: TanStack caches across a clinic switch, and a key of
 * `['sessions', page]` would hand clinic B's staff clinic A's treatment list
 * out of the cache for as long as it stayed fresh. That is a patient-data leak,
 * not a stale number.
 *
 * They live in the feature rather than in the panel-wide factory because the
 * factory is shared with agents working in parallel on other sections; a
 * feature-local namespace cannot collide with theirs.
 */

export const sessionKeys = {
  root: (clinicId: string | null) => ['sessions', clinicId] as const,

  /** Branch-aware: `branch` is the switcher's `branchKey`. */
  list: (clinicId: string | null, params: Record<string, unknown>, branch = 'all') =>
    ['sessions', clinicId, 'list', branch, params] as const,

  detail: (clinicId: string | null, sessionId: string) =>
    ['sessions', clinicId, 'detail', sessionId] as const,

  consentStatus: (clinicId: string | null, sessionId: string) =>
    ['sessions', clinicId, 'consent-status', sessionId] as const,

  /**
   * The same question asked before a session row exists — patient + service.
   * Under the section root on purpose: every consent write invalidates that
   * root (see `consent/api.ts`), so a signature taken in the take-consent
   * drawer turns the form's card green without any wiring of its own.
   */
  consentRequirement: (
    clinicId: string | null,
    patientId: string,
    procedureId: string,
    clinicProcedureId: string,
  ) =>
    [
      'sessions',
      clinicId,
      'consent-requirement',
      patientId,
      procedureId,
      clinicProcedureId,
    ] as const,

  consumptionSuggestions: (clinicId: string | null, params: Record<string, unknown>) =>
    ['sessions', clinicId, 'consumption-suggestions', params] as const,

  /**
   * The lots a session may draw from — those at the SESSION's branch (a
   * multi-branch clinic draws only from the shelf of the branch the treatment
   * happened at; a foreign-branch lot is refused with BRANCH_MISMATCH).
   */
  lotsForMaterial: (clinicId: string | null, materialId: string, branch = 'all') =>
    ['sessions', clinicId, 'lots', materialId, branch] as const,

  overrideBaseline: (
    clinicId: string | null,
    materialId: string,
    quantityMilli: number,
    branch = 'all',
  ) => ['sessions', clinicId, 'override-baseline', materialId, quantityMilli, branch] as const,

  injectionMap: (clinicId: string | null, sessionId: string) =>
    ['sessions', clinicId, 'injection-map', sessionId] as const,

  injectionTemplates: (clinicId: string | null, procedureId: string | null) =>
    ['sessions', clinicId, 'injection-templates', procedureId] as const,

  procedures: (clinicId: string | null) => ['sessions', clinicId, 'procedures'] as const,
  doctors: (clinicId: string | null) => ['sessions', clinicId, 'doctors'] as const,
  rooms: (clinicId: string | null) => ['sessions', clinicId, 'rooms'] as const,
  patient: (clinicId: string | null, patientId: string) =>
    ['sessions', clinicId, 'patient', patientId] as const,
} as const;
